import {
  amenityPacks,
  CUSTOM_ADDITIONS_SECTION_WEIGHT,
  getAmenityPack,
  isPackCustomized,
  normalizeScoreConfig,
  resolveScoreBands,
} from "./amenityPacks";
import { DEFAULT_RADIUS_METERS, formatDistance } from "./filters";
import type {
  AggregateBreakdownCount,
  AggregateScoreSection,
  AmenityPackId,
  ConstraintLevel,
  ResolvedScoreBand,
  ScoreConfig,
  ScoreMode,
} from "./types";

interface ScoreSectionDefinition {
  id: string;
  label: string;
  weight: number;
  types: string[];
}

interface ScoreProfile {
  id: AmenityPackId;
  label: string;
  description: string;
  scoreMode: ScoreMode;
  sections: ScoreSectionDefinition[];
}

const TYPE_SCORE_TARGETS: Record<string, number> = {
  grocery_store: 2,
  pharmacy: 1.5,
  doctor: 1.5,
  school: 1.2,
  preschool: 1,
  park: 1.5,
  transit_station: 3,
  cafe: 4,
  restaurant: 6,
  gym: 1.5,
  shopping_mall: 0.8,
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function roundToTenth(value: number): number {
  return Math.round(value * 10) / 10;
}

function roundToHundredth(value: number): number {
  return Math.round(value * 100) / 100;
}

function unique<T>(items: T[]): T[] {
  return [...new Set(items)];
}

function summarizeScore(label: string, score: number, scoreMode: ScoreMode): string {
  if (scoreMode === "gap") {
    if (score >= 8) {
      return `${label} shows a meaningful nearby coverage gap.`;
    }

    if (score >= 5) {
      return `${label} looks somewhat undersupplied in the closer bands.`;
    }

    return `${label} already has decent nearby coverage.`;
  }

  if (score >= 8) {
    return `${label} looks strong around this site, with good close-in support.`;
  }

  if (score >= 6) {
    return `${label} looks solid, though the nearest band is not dominant.`;
  }

  if (score >= 4) {
    return `${label} is present, but support is uneven across the walkable area.`;
  }

  return `${label} looks thin nearby and may rely on broader-area support.`;
}

function getTypeTarget(type: string, radiusMeters: number): number {
  const baseTarget = TYPE_SCORE_TARGETS[type] ?? 2;
  const radiusScale = clamp(radiusMeters / DEFAULT_RADIUS_METERS, 0.35, 2.25);

  return roundToHundredth(baseTarget * radiusScale);
}

function getConstraintLevel(attainment: number): ConstraintLevel {
  if (attainment >= 0.85) {
    return "strong";
  }

  if (attainment >= 0.55) {
    return "balanced";
  }

  return "constrained";
}

function formatWeight(weight: number): string {
  return `${Math.round(weight * 100)}%`;
}

function buildMethodology(scoreMode: ScoreMode, resolvedBands: ResolvedScoreBand[]): string {
  const bandSummary = resolvedBands
    .map((band) => `${formatDistance(band.radiusMeters)} × ${formatWeight(band.weight)}`)
    .join(", ");

  if (scoreMode === "gap") {
    return `Band weights from nearest to widest are ${bandSummary}. Higher values mean thinner local coverage against the curated target counts.`;
  }

  return `Band weights from nearest to widest are ${bandSummary}. Scores use diminishing returns against the curated target counts, so early nearby coverage matters more than excess volume.`;
}

function buildCustomProfile(selectedTypes: string[]): ScoreProfile {
  return {
    id: "custom",
    label: "Custom mix",
    description: "A custom proximity read for the currently selected amenity mix.",
    scoreMode: "fit",
    sections: [
      {
        id: "selected-fit",
        label: "Selected fit",
        weight: 1,
        types: selectedTypes,
      },
    ],
  };
}

function resolveScoreProfile(activePackId: AmenityPackId, selectedTypes: string[]): ScoreProfile {
  const pack = getAmenityPack(activePackId);

  if (!pack) {
    return buildCustomProfile(selectedTypes);
  }

  const selectedSet = new Set(selectedTypes);
  const packSectionWeight = pack.sections.reduce((sum, section) => sum + section.weight, 0) || 1;
  const nativeWeightScale = selectedTypes.some((type) => !pack.defaultTypes.includes(type))
    ? 1 - CUSTOM_ADDITIONS_SECTION_WEIGHT
    : 1;
  const sections = pack.sections.map((section) => ({
    ...section,
    weight: roundToHundredth((section.weight / packSectionWeight) * nativeWeightScale),
  }));
  const customTypes = selectedTypes.filter((type) => !pack.defaultTypes.includes(type));

  if (customTypes.length > 0) {
    sections.push({
      id: "custom-additions",
      label: "Custom additions",
      weight: CUSTOM_ADDITIONS_SECTION_WEIGHT,
      types: unique(customTypes),
    });
  }

  return {
    id: pack.id,
    label: pack.label,
    description: pack.description,
    scoreMode: pack.scoreMode,
    sections,
  };
}

export function buildResolvedScoreBands(
  radiusMeters: number,
  scoreConfig: ScoreConfig,
  activePackId: AmenityPackId,
): ResolvedScoreBand[] {
  return resolveScoreBands(radiusMeters, normalizeScoreConfig(scoreConfig, activePackId));
}

export function buildScoredCount(
  type: string,
  radiusMeters: number,
  scoreMode: ScoreMode,
  cumulativeCounts: Array<{
    fraction: number;
    radiusMeters: number;
    weight: number;
    cumulativeCount: number;
  }>,
): AggregateBreakdownCount {
  let previousCount = 0;
  let weightedCount = 0;
  const target = getTypeTarget(type, radiusMeters);

  const bands = cumulativeCounts.map((band) => {
    const bandCount = Math.max(0, band.cumulativeCount - previousCount);
    previousCount = band.cumulativeCount;
    weightedCount += bandCount * band.weight;

    return {
      fraction: band.fraction,
      radiusMeters: band.radiusMeters,
      weight: band.weight,
      cumulativeCount: band.cumulativeCount,
      bandCount,
    };
  });

  const attainment = clamp(1 - Math.exp(-(weightedCount / Math.max(target, 0.1))), 0, 1);
  const scoreValue = scoreMode === "gap" ? Math.exp(-(weightedCount / Math.max(target, 0.1))) : attainment;

  return {
    type,
    count: cumulativeCounts[cumulativeCounts.length - 1]?.cumulativeCount ?? 0,
    weightedCount: roundToTenth(weightedCount),
    target,
    attainment: roundToHundredth(attainment),
    constraintLevel: getConstraintLevel(attainment),
    score: roundToTenth(scoreValue * 10),
    bands,
  };
}

export function buildScoreSections(
  counts: AggregateBreakdownCount[],
  radiusMeters: number,
  selectedTypes: string[],
  activePackId: AmenityPackId,
  scoreConfig: ScoreConfig,
): {
  overallScore: number;
  activePackId: AmenityPackId;
  packLabel: string;
  packDescription: string;
  scoreMode: ScoreMode;
  isPackCustomized: boolean;
  scoreConfig: ScoreConfig;
  resolvedBands: ResolvedScoreBand[];
  methodology: string;
  sections: AggregateScoreSection[];
} {
  const normalizedScoreConfig = normalizeScoreConfig(scoreConfig, activePackId);
  const resolvedBands = buildResolvedScoreBands(radiusMeters, normalizedScoreConfig, activePackId);
  const profile = resolveScoreProfile(activePackId, selectedTypes);
  const byType = new Map(counts.map((count) => [count.type, count]));
  let totalWeight = 0;
  let weightedScore = 0;

  const sections = profile.sections
    .map((section) => {
      const sectionCounts = section.types
        .map((type) => byType.get(type))
        .filter((value): value is AggregateBreakdownCount => Boolean(value));

      if (sectionCounts.length === 0) {
        return null;
      }

      const sectionScore = roundToTenth(
        sectionCounts.reduce((sum, item) => sum + item.score, 0) / sectionCounts.length,
      );

      totalWeight += section.weight;
      weightedScore += sectionScore * section.weight;

      return {
        id: section.id,
        label: section.label,
        score: sectionScore,
        summary: summarizeScore(section.label, sectionScore, profile.scoreMode),
      };
    })
    .filter((value): value is AggregateScoreSection => Boolean(value));

  return {
    overallScore: totalWeight > 0 ? roundToTenth(weightedScore / totalWeight) : 0,
    activePackId: profile.id,
    packLabel: profile.label,
    packDescription: profile.description,
    scoreMode: profile.scoreMode,
    isPackCustomized: isPackCustomized(profile.id, selectedTypes, normalizedScoreConfig),
    scoreConfig: normalizedScoreConfig,
    resolvedBands,
    methodology: buildMethodology(profile.scoreMode, resolvedBands),
    sections,
  };
}

export const amenityResearchSources = [
  {
    label: "Google real estate Aggregate example",
    href: "https://developers.google.com/maps/documentation/places-aggregate/overview#real_estate_investment_firm",
  },
  {
    label: "Google custom location score guidance",
    href: "https://developers.google.com/maps/architecture/places-insights-location-score",
  },
  {
    label: "Apartments.com renter location priorities",
    href: "https://www.apartments.com/grow/learning-center/location-multifamily-marketing",
  },
  {
    label: "NMHC and Kingsley survey summary",
    href: "https://aoausa.com/new-largest-ever-apartment-rental-survey-highlights-amenities-consumers-want-by-the-national-multifamily-housing-council/",
  },
  {
    label: "Equity Apartments Parc East neighborhood page",
    href: "https://www.equityapartments.com/new-york-city/gramercy-park-murray-hill/parc-east-apartments",
  },
  {
    label: "Equity Apartments The Cleo neighborhood page",
    href: "https://www.equityapartments.com/los-angeles/koreatown/the-cleo-apartments",
  },
] as const;

export const availableAmenityPackIds = amenityPacks.map((pack) => pack.id);
